import { useMutation, useQuery } from "@tanstack/react-query";
import { addDComment, getDContactUs } from "api/developmentResearchUser";
import { CustomBreadCrump } from "components/General/CustomBreadCrump";
import { CustomButton } from "components/General/CustomButton";
import CustomTextArea from "components/General/CustomTextArea";
import { CustomTextField } from "components/General/CustomTextField";
import PageTitle from "components/General/PageTitle";
import { EXCEPTIONTYPES } from "constants/enum_exception_types";
import { VALIDATION } from "constants/enum_validation_messages";
import { useFormik } from "formik";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import exception from "utils/exception";
import { errorHandler } from "utils/exception/errorHandler";
import * as Yup from "yup";

type IContactForm = {
  Phone: string;
  Name: string;
  Description: string;
};

const ContactUs = () => {
  const router = useRouter();
  const { locale } = router;
  const { t } = useTranslation();

  const FORM_SHCEMA = {
    Phone: Yup.string()
      .required(t(VALIDATION.REQUIRED) as string)
      .matches(/^09[0-9]{9}/, t(VALIDATION.PHONE) as string),
    Name: Yup.string()
      .required(t(VALIDATION.REQUIRED) as string)
      .min(3, "نام و نام‌خانوادگی حداقل باید 3 کاراکتر باشد"),
    Description: Yup.string().required(t(VALIDATION.REQUIRED) as string),
  };

  const { data } = useQuery(["getDContactUs", locale], () => getDContactUs(locale));

  const { mutate } = useMutation(addDComment, {
    onError: (err: any) => {
      exception.message([
        { title: errorHandler(err.response) as string, type: EXCEPTIONTYPES.ERROR },
      ]);
    },
    onSuccess: (res) => {
      formik.resetForm();
      exception.message([{ title: res.data, type: EXCEPTIONTYPES.SUCCESS }]);
    },
  });

  const submit = async (values: IContactForm) => {
    mutate(values);
  };

  const formik = useFormik({
    initialValues: {
      Phone: "",
      Name: "",
      Description: "",
    },
    validationSchema: Yup.object(FORM_SHCEMA),
    onSubmit: submit,
  });

  return (
    <section>
      <div className="container">
        <CustomBreadCrump
          data={[
            {
              title: t("ClinicalResearchDevelopmentCenter"),
              href: "/research",
            },
            { title: t("CommunicationWithTheUnit"), href: "/" },
          ]}
        />
      </div>
      <div className="container mt-16">
        <PageTitle title={t("SubmitCommentSystem")} />
      </div>
      <div className=" mt-32 bg-gray-F7F7F7 py-48 px-8 md:px-24">
        <div className="container">
          <div className="grid grid-cols-1 md:mx-32 md:grid-cols-2 ">
            <div
              className={`relative h-[357px] rounded-5 ${"ltr:before:left-[-32px] rtl:before:right-[-32px]"} before:bottom-0 before:h-[162px]  before:w-12  before:rounded-3  before:bg-primary-main  md:before:absolute`}
            >
              <Image className="rounded-5" alt="" src={"/assets/contact.png"} fill />
            </div>
            <form
              onSubmit={formik.handleSubmit}
              className=" grid grid-cols-1 items-start gap-24 rounded-12 bg-gray-F7F7F7 px-24 py-16 md:grid-cols-2"
            >
              <CustomTextField
                name="Name"
                labelText={t("FullName") as string}
                placeholder={t("EnterName") as string}
                formik={formik}
                maxChars={60}
              />
              <CustomTextField
                name="Phone"
                labelText={t("MobileNumber") as string}
                placeholder={t("EnterPhone") as string}
                formik={formik}
                maxChars={11}
              />

              <div className="col-span-full">
                <CustomTextArea
                  rows={4}
                  name="Description"
                  labelText={t("CommentDescription") as string}
                  placeholder={t("EnterDescription") as string}
                  formik={formik}
                />
              </div>

              <div className="col-span-full flex justify-end">
                <CustomButton type="submit" className="w-[229px] !rounded-full">
                  {t("SubmitComment")}
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="container mt-64 mb-[120px]">
        <PageTitle title={t("WaysOfCommunication")} />

        <div className="mt-48 grid grid-cols-1 gap-24 md:grid-cols-2">
          {data?.data?.emails?.map((el: any, i: number) => {
            return (
              <div
                key={i}
                className="flex items-center justify-between rounded-15 border border-gray-D0D0D0 py-24 px-16"
              >
                <p className="text-16 text-gray-4A4A4A">ایمیل واحد </p>
                <p className="text-20 text-gray-151618">{el.email}</p>
              </div>
            );
          })}
          {data?.data?.phones?.map((el: any, i: number) => {
            return (
              <div
                key={i}
                className="flex items-center justify-between rounded-15 border border-gray-D0D0D0 py-24 px-16"
              >
                <p className="text-16 text-gray-4A4A4A">{el.title}</p>
                <p className="text-20 text-gray-151618">{el.number}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { ContactUs };
